import { ApexOptions } from "apexcharts";
import { computed, ComputedRef } from "vue";
import {
  MyKidReportContentsConstType,
  SurveyQuestionType,
} from "src/types/constType";

export default function useConst() {
  const rules = {
    requireRules: (v: string): boolean | string => {
      const regExp = /\S/g;
      return v
        ? regExp.test(v) || "공백은 입력할 수 없습니다."
        : "필수 값입니다.";
    },
    emailRules: (v: string): boolean | string =>
      /.+@.+\..+/.test(v) || "정확한 이메일 주소가 아닙니다.",
    phoneRules: (v: string): boolean | string => {
      const regExp =
        /01([0|1|6|7|8|9])-?(\d{3,4})-?(\d{4})|01([0|1|6|7|8|9])(\d{7,8})/;
      if (v) {
        return regExp.test(v) || "휴대전화 형식이 올바르지 않습니다.";
      }
      return false;
    },
    maxLengthRules: (v: string, num: number): boolean | string => {
      return (v && v.length <= num) || `${num} 자 이하로 입력해주세요.`;
    },
    maxNumRules: (v: string, num: number): boolean | string => {
      return (v && parseInt(v) <= num) || `${num} 이하로 입력해주세요.`;
    },
    minLengthRules: (v: string, num: number): boolean | string => {
      return (v && v.length >= num) || `${num} 자 이상 입력해주세요.`;
    },
  };

  const PolicyConst = {
    TermsOfService: "TermsOfService",
    Privacy: "Privacy",
    Refund: "Refund",
    Charged: "Charged",
  } as const;

  const AnalysisCriteriaConst = [
    {
      key: "자기주도",
      value: [
        "스스로 일어나기",
        "손 씻기",
        "아침 약 챙겨먹기",
        "저녁 약 챙겨먹기",
        "저녁에 양치질하기",
      ],
    },
    {
      key: "자기조절",
      value: [
        "약속한 시간 만큼 컴퓨터 게임하기",
        "약속한 시간 만큼 TV 보기",
        "약속한 시간 만큼 유튜브 보기",
        "자기 전에 핸드폰 내기",
      ],
    },
    {
      key: "적응행동",
      value: ["아침 밥 앉아서 먹기", "신발 정리하기", "외투 정리하기"],
    },
    {
      key: "인지처리리",
      value: ["일기 쓰기", "책 읽기", "하루 마무리하기"],
    },
    {
      key: "학업수행",
      value: [
        "아침에 온라인 수업 준비하기",
        "알림장 식탁에 올려두기",
        "집중해서 공부하기",
        "밤에 내일 학교 갈 준비하기",
      ],
    },
  ];

  const radarChartOptions: ComputedRef<ApexOptions> = computed(() => {
    return {
      chart: {
        type: "radar",
        parentHeightOffset: 0,
        toolbar: { show: false },
      },

      plotOptions: {
        radar: {
          polygons: {
            strokeColors: "#d3d3d3",
          },
        },
      },
      dataLabels: {
        enabled: true,
        formatter(val: string | number | number[]): string | number {
          return val + "%";
        },
        style: {
          colors: ["#222222"],
          fontWeight: 400,
          fontSize: "0.5rem",
        },
        background: {
          enabled: false,
        },
      },
      colors: ["#FCA75C"],

      xaxis: {
        labels: {
          show: true,
          style: {
            fontSize: "0.625rem",
            colors: new Array(5).fill("#222222"),
          },
        },
      },
      markers: {
        size: 0,
      },
      yaxis: {
        min: 0,
        max: 100,
        tickAmount: 4,
        show: false,
      },
    };
  });

  const TaskConst = {
    alarm: "alarm",
    bag: "bag",
    book: "book",
    breakfast: "breakfast",
    brush: "brush",
    coat: "coat",
    diary: "diary",
    edrug: "edrug",
    game: "game",
    hand: "hand",
    inform: "inform",
    mdrug: "mdrug",
    night: "night",
    online: "online",
    phone: "phone",
    question: "question",
    read: "read",
    shoes: "shoes",
    tv: "tv",
    youtube: "youtube",
  } as const;

  const surveyQuestions: [SurveyQuestionType[], SurveyQuestionType[]] = [
    [
      {
        index: 1,
        category: "",
        question: "우리 아이는 모양에 따라 정확하게 그릴 수 있다.",
      },
      {
        index: 2,
        category: "",
        question: "우리 아이는 혼자 신발을 신고 벗는다.",
      },
      {
        index: 3,
        category: "",
        question:
          "우리 아이는 외부 활동에서 대소변 등 생리적 현상을 조절할 수 있다.",
      },
      {
        index: 4,
        category: "",
        question: "우리 아이는 받침 있는 글자를 쓸 수 있다. (예: 책, 운동장..)",
      },
      {
        index: 5,
        category: "",
        question: "우리 아이는 크고 작은 크기를 구분한다.",
      },
      {
        index: 6,
        category: "",
        question: "우리 아이는 순서에 맞게 이야기를 말할 수 있다.",
      },
      {
        index: 7,
        category: "",
        question: "우리 아이는 어려움이 있는 친구를 도와주곤 한다.",
      },
      {
        index: 8,
        category: "",
        question: "우리 아이는 스스로에 대해 긍정적으로 생각한다.",
      },
      {
        index: 9,
        category: "",
        question:
          "우리 아이는 다른 사람이 실수한 일을 너그럽게 받아들일 수 있다.",
      },
      {
        index: 10,
        category: "",
        question:
          "우리 아이는 기본적인 예의를 알고 표현하곤 한다 (예: 고마워, 미안해..)",
      },
      {
        index: 11,
        category: "",
        question: "우리 아이는 물건을 사용한 후, 사용한 물건을 제자리에 둔다.",
      },
      {
        index: 12,
        category: "",
        question: "우리 아이는 자기 물건을 스스로 잘 챙긴다.",
      },
    ],
    [
      {
        index: 1,
        category: "neat",
        question: "우리 아이는 방을 엉망인 채로 남겨 둔다.",
      },
      {
        index: 2,
        category: "responsibility",
        question:
          "우리 아이는 학교나 공공장소에서 이미 정해져 있는 규칙을 어긴다.",
      },
      {
        index: 3,
        category: "sadness",
        question: "우리 아이는 쉽게 속상해한다.",
      },
      {
        index: 4,
        category: "attention",
        question:
          "우리 아이는 어떠한 활동에 대하여 주의를 집중 하는데 어려움이 있다.",
      },
      {
        index: 5,
        category: "mindful",
        question:
          "우리 아이는 준비물, 숙제 등 숙제를 하는데 필요한 물건들을 잃어 버리곤 한다.",
      },
      {
        index: 6,
        category: "responsibility",
        question: "우리 아이는 다른 친구들보다 통제가 안되는 것 같다.",
      },
      {
        index: 7,
        category: "resilience",
        question: "우리 아이는 비활동적이고 행동이 느리며 기운이 없는 것 같다.",
      },
      {
        index: 8,
        category: "neat",
        question: "우리 아이는 방을 지저분하게 사용한다.",
      },
      {
        index: 9,
        category: "responsibility",
        question:
          "우리 아이는 친구들과 함께 있을 때, 다른 친구들보다 더 제멋대로 행동하는 것 같다.",
      },
      {
        index: 10,
        category: "achievement",
        question: "우리 아이는 숙제처럼 평가 되는 활동도 잘 하지 않는다.",
      },
      {
        index: 11,
        category: "responsibility",
        question: "우리 아이는 학교 선생님의 말을 안 듣는다.",
      },
      {
        index: 12,
        category: "achievement",
        question: "우리 아이는 숙제나 공부를 시작하기를 어려워 한다.",
      },
      {
        index: 13,
        category: "monitoring",
        question:
          "우리 아이는 자신의 행동이 다른 사람에게 어떤 영향을 미치는지 가늠하지 못하는 것 같다.",
      },
      {
        index: 14,
        category: "achievement",
        question:
          "우리 아이는 숙제와 같이 노력이 필요한 활동을 끝내는 것을 어려워 한다.",
      },
      {
        index: 15,
        category: "attention",
        question: "우리 아이는 어떤 일에 오랫동안 주의를 기울이지 못한다.",
      },
      {
        index: 16,
        category: "sadness",
        question: "우리 아이는 잘 우는 편이다.",
      },
      {
        index: 17,
        category: "anger",
        question: "우리 아이는 갑자기 화를 내곤 한다.",
      },
      {
        index: 18,
        category: "mindful",
        question: "우리 아이는 자신의 물건을 잘 잃어버린다.",
      },
      {
        index: 19,
        category: "resilience",
        question: "우리 아이는 슬퍼하거나 우울해 하곤 한다.",
      },
      {
        index: 20,
        category: "achievement",
        question:
          "우리 아이는 공부와 같이 꾸준한 노력이 필요한 활동을 하지 않으려 한다.",
      },
      {
        index: 21,
        category: "monitoring",
        question:
          "우리 아이는 스스로의 어떤 행동이 다른 사람을 곤란하게 한다는 것을 깨닫지 못한다.",
      },
      {
        index: 22,
        category: "neat",
        question:
          "우리 아이가 물건을 어질러 놓고 가서 다른 사람이 치워야 한다.",
      },
      {
        index: 23,
        category: "attention",
        question: "우리 아이는 집중을 잘 못하고 쉽게 산만해진다.",
      },
      {
        index: 24,
        category: "sadness",
        question: "우리 아이는 쉽게 울먹인다.",
      },
      {
        index: 25,
        category: "monitoring",
        question:
          "우리 아이는 자신의 행동이 남들에게 부정적인 반응을 불러 일으키는지를 알아차리지 못한다.",
      },
      {
        index: 26,
        category: "anger",
        question: "우리 아이는 기분이 자주 변한다.",
      },
      {
        index: 27,
        category: "mindful",
        question: "우리 아이는 숙제, 가정통신문 등을 집에 가져오지 않곤 한다.",
      },
      {
        index: 28,
        category: "anger",
        question: "우리 아이는 사소한 일에도 쉽게 폭발한다.",
      },
    ],
  ];

  const MyKidReportContentsConst: MyKidReportContentsConstType = {
    selfControl: {
      Attention: {
        key: "Attention",
        title: "주의력",
        avg: 62,
        content:
          "주의력은 특정 상황에서 집중을 유지하는 능력을 말합니다. 목표를 달성하기 위해 필요한 능력입니다.",
      },
      Achievement: {
        key: "Achievement",
        title: "성취 추구",
        avg: 60,
        content:
          "성취 추구는 어떤 목표를 달성하려는 강한 의지와 노력을 의미합니다. 아이의 꾸준함을 요구합니다.",
      },
      Responsibility: {
        key: "Responsibility",
        title: "책임 의식",
        avg: 62,
        content:
          "책임의식은 자신의 의무와 역할을 성실히 수행하려는 능력을 의미합니다. 다른 사람들과 협력하기 위해 필요합니다.",
      },
    },
    mood: {
      Resilience: {
        key: "Resilience",
        title: "자아 탄력성",
        avg: 67,
        content:
          "자아탄력성은 어려운 상황에서도 긍정적으로 생각하고, 어려움을 극복하려는 능력입니다.",
      },
      Sadness: {
        key: "Sadness",
        title: "슬픔 조절",
        avg: 49,
        content:
          "어떤 상황에서 슬픔이나 우울함을 적절하게 다루고 관리하는 능력을 말합니다. 아이의 정서적 안정을 위해 필요합니다.",
      },
      Anger: {
        key: "Anger",
        title: "분노 조절",
        avg: 57,
        content:
          "화를 적절하게 다루고 통제하는 능력을 말합니다. 자신과 주변 사람들 사이에서 좋은 관계를 맺을 수 있습니다.",
      },
    },
    metaCognitive: {
      Mindful: {
        key: "Mindful",
        title: "챙김력",
        avg: 62,
        content:
          "일상 생활에서 필요한 물건, 정보를 관리하는 능력을 의미합니다. 자기 관리 능력과 직결되는 영역입니다.",
      },
      Monitoring: {
        key: "Monitoring",
        title: "모니터링",
        avg: 55,
        content:
          "주어진 상황에서 자신의 현재 상태에 대해 주의 깊게 관찰하고 이해하는 능력을 의미합니다.",
      },
      Neat: {
        key: "Neat",
        title: "정리 정돈",
        avg: 37,
        content:
          "물건이나 환경을 깨끗하고 체계적으로 정리하는 능력을 의미합니다. 아이 스스로 책임감을 높이는데에도 작용합니다.",
      },
    },

    schoolPreparation: {
      data: {
        key: "schoolPreparation",
        title: "학교 준비도",
        avg: 52,
        content:
          "학교 준비도는 학교 생활을 하기 위하여 우리 아이가 얼마나 준비되어 있는지를 말합니다. 주차별 상태를 확인해보세요.",
      },
    },
  };

  const myKidStateEvaluationBasicInfo = [
    {
      label: "selfControl",
      value: [
        {
          component: "selfControl",
          score: 0,
          isExpanded: true,
        },
        {
          component: "Attention",
          score: 0,
          isExpanded: true,
        },
        { component: "Responsibility", score: 0, isExpanded: true },
        { component: "Achievement", score: 0, isExpanded: true },
      ],
    },
    {
      label: "mood",
      value: [
        { component: "mood", score: 0, isExpanded: true },
        {
          component: "Resilience",
          score: 0,
          isExpanded: true,
        },
        {
          component: "Sadness",
          score: 0,
          isExpanded: true,
        },
        { component: "Anger", score: 0, isExpanded: true },
      ],
    },
    {
      label: "metaCognitive",
      value: [
        { component: "metaCognitive", score: 0, isExpanded: true },
        {
          component: "Monitoring",
          score: 0,
          isExpanded: true,
        },
        {
          component: "Neat",
          score: 0,
          isExpanded: true,
        },
        { component: "Mindful", score: 0, isExpanded: true },
      ],
    },
  ];

  const model = "B2B2C";

  const regions = [
    "서울특별시",
    "부산광역시",
    "대구광역시",
    "인천광역시",
    "광주광역시",
    "대전광역시",
    "울산광역시",
    "세종특별자치시",
    "경기도",
    "충청북도",
    "충청남도",
    "전라남도",
    "경상북도",
    "경상남도",
    "제주특별자치도",
    "강원특별자치도",
    "전북특별자치도",
  ];

  const cntScored = {
    magicBall: 100 / 4,
    magicPotion: 100 / 12,
    magicGift: 100 / 3,
  };

  return {
    rules,
    AnalysisCriteriaConst,
    radarChartOptions,
    PolicyConst,
    TaskConst,
    surveyQuestions,
    MyKidReportContentsConst,
    myKidStateEvaluationBasicInfo,
    model,
    regions,
    cntScored,
  };
}
